import { useRef, useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import DocumentAPI from 'api/document/document';
import { normalizeFileName } from 'utils/normalizeFileName';

export const SignatureCanvas: React.FC<{
  onSignatureUuidChange: (uuid: string) => void,
  error?: boolean
}> = ({ onSignatureUuidChange, error }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [signatureUuid, setSignatureUuid] = useState<string>('');

  const startDrawing = (e: React.MouseEvent) => {
    if (isConfirmed) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setIsDrawing(true);
    setHasInteracted(true);

    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  const draw = (e: React.MouseEvent) => {
    if (!isDrawing || isConfirmed) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  }

  const stopDrawing = () => {
    if (isConfirmed) return;
    setIsDrawing(false);
  }

  const clearCanvas = () => {
    if (isConfirmed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInteracted(false);
    setSignatureUuid('');
  }

  const base64ToFile = (base64: string, filename: string): File => {
    const arr = base64.split(',');
    const mime = RegExp(/:(.*?);/).exec(arr[0])?.[1] ?? 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const uploadSignature = async (signatureBase64: string): Promise<string> => {
    try {
      setIsUploading(true);
      const timestamp = new Date().getTime();
      const originalFileName = `assinatura_${timestamp}.png`;
      const normalizedFileName = normalizeFileName(originalFileName);
      const signatureFile = base64ToFile(signatureBase64, normalizedFileName);

      const response = await DocumentAPI.upload({
        documentInfo: {
          type: 'DOCUMENTO DE PARCEIRIA',
        },
        file: signatureFile
      });

      return response.uuid;
    } catch (error) {
      console.error('Erro ao fazer upload da assinatura:', error);
      throw new Error('Falha no upload da assinatura: ' + (error as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  const confirmSignature = async () => {
    if (!hasInteracted) return;

    const canvas = canvasRef.current;
    if (canvas) {
      try {
        const signature = canvas.toDataURL();

        // Fazer upload da assinatura
        const uuid = await uploadSignature(signature);
        setSignatureUuid(uuid);

        // Notificar o componente pai sobre o UUID
        onSignatureUuidChange(uuid);

        setIsConfirmed(true);
      } catch (error) {
        console.error('Erro ao confirmar assinatura:', error);
        alert('Erro ao salvar assinatura: ' + (error as Error).message);
      }
    }
  }

  const revertSignature = () => {
    setIsConfirmed(false);
    setSignatureUuid('');
    clearCanvas();

    // Notificar o componente pai que o UUID foi limpo
    onSignatureUuidChange('');
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 800;
    canvas.height = 200;

    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const canConfirm = hasInteracted && !isConfirmed && !isUploading;

  const getBorderStyle = () => {
    if (error) {
      return '2px solid #d32f2f';  // Error style
    } else if (isConfirmed) {
      return '2px solid #4caf50';  // Success style
    } else {
      return '2px solid #ccc';  // Default style
    }
  };

  return (
    <Box width="100%" display="flex" flexDirection="column" alignItems="center">
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        style={{
          border: getBorderStyle(),
          borderRadius: '4px',
          cursor: isConfirmed ? 'not-allowed' : 'crosshair',
          width: '800px',
          height: '200px',
          opacity: isConfirmed ? 0.8 : 1,
        }}
      />
      <Box display="flex" gap={2} mt={1} justifyContent="center">
        <Button
          onClick={clearCanvas}
          size="small"
          variant="outlined"
          disabled={isConfirmed}
        >
          Limpar Assinatura
        </Button>
        <Button
          onClick={confirmSignature}
          size="small"
          variant="contained"
          color="success"
          disabled={!canConfirm}
          startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
        >
          {isUploading ? 'Salvando...' : 'Confirmar Assinatura'}
        </Button>
        {isConfirmed && (
          <Button
            onClick={revertSignature}
            size="small"
            variant="outlined"
            color="warning"
          >
            Reverter Assinatura
          </Button>
        )}
      </Box>
      {isConfirmed && (
        <Typography variant="caption" color="success.main" mt={1} display="block" textAlign="center">
          ✓ Assinatura confirmada e salva no servidor
        </Typography>
      )}
      {!hasInteracted && (
        <Typography variant="caption" color="text.secondary" mt={1} display="block" textAlign="center">
          Desenhe sua assinatura para poder confirmar
        </Typography>
      )}
    </Box>
  );
}
